import { MACHINE_API_URLS } from "@/config/apiConfig";
import { MachineResponse } from "@/types/machine/machine";



// Helper function to get auth token
const getAuthToken = (): string => {
  if (typeof window === "undefined") return "";
  const token = localStorage.getItem("token");
  return token || "";
};

// Get All Machines
export const getAllMachines = async () => {
  const token = getAuthToken();
  const response = await fetch(MACHINE_API_URLS.GET_ALL_MACHINES, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  if (!response.ok) throw new Error("Failed to get all machines");

  const result = await response.json();
  // Frappe API can return { data: [...] } or { data: { 0: {...}, 1: {...} } } (object with numeric keys)
  const raw = result.data ?? result;
  const data: MachineResponse[] = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
  return data;
};

// Get Machine by ID
export const getMachineById = async (name: string) => {
    const token = getAuthToken();
    const response = await fetch(MACHINE_API_URLS.GET_MACHINE_BY_ID.replace(':name', name), {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: token,
        },
    });
    if (!response.ok) throw new Error("Failed to get machine by id");
    const result = await response.json();
    // Frappe API returns data in { data: {...} } format
    const data: MachineResponse = result.data || result;
    return data;
};

// Get Machine by device_eui (single machine, real API data)
export const getMachineByDeviceEui = async (deviceEui: string): Promise<MachineResponse | null> => {
  const token = getAuthToken();
  const filters = encodeURIComponent(JSON.stringify([["device_eui", "=", deviceEui]]));
  const url = `${MACHINE_API_URLS.GET_MACHINE_BY_DEVICE_EUI}&filters=${filters}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });
  if (!response.ok) throw new Error("Failed to get machine by device_eui");
  const result = await response.json();
  const raw = result.data ?? result;
  const list = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.values(raw) : [];
  const machine = list[0] as MachineResponse | undefined;
  return machine ?? null;
};

// Create Machine
export const createMachine = async (machine: any) => {
    const token = getAuthToken();
    const response = await fetch(MACHINE_API_URLS.CREATE_MACHINE, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: token,
        },
        body: JSON.stringify(machine),
    });
    if (!response.ok) throw new Error("Failed to create machine");
    const data: any = await response.json();
    return data;

}

// Update Machine
export const updateMachine = async (name: string, machine: any) => {
    const token = getAuthToken();
    const response = await fetch(MACHINE_API_URLS.UPDATE_MACHINE.replace(':name', name), {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: token,
        },
        body: JSON.stringify(machine),
    });
    if (!response.ok) throw new Error("Failed to update machine");
    const data: any = await response.json();
    return data;
}

// Delete Machine
export const deleteMachine = async (name: string) => {
    const token = getAuthToken();
    const response = await fetch(MACHINE_API_URLS.DELETE_MACHINE.replace(':name', name), {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Authorization: token,
        },
    });
    if (!response.ok) throw new Error("Failed to delete machine");
    const data: any = await response.json();
    return data;
}

// Get All Machines and Prices
export const getAllMachinesAndPrices = async () => {
    const token = getAuthToken();
    try {
        const response = await fetch(MACHINE_API_URLS.GET_ALL_MACHINES_AND_PRICES, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: token,
            },
        });
        if (!response.ok) {
            throw new Error(`Failed to get all machines and prices: ${response.status} ${response.statusText}`);
        }
        const data: any = await response.json();
        // Handle both { data: [...] } and direct array responses
        return data;
    } catch (error) {
        console.error('Error fetching machines and prices:', error);
        throw error;
    }
}